import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { STRIPE_PUBLIC_KEY } from '../config';
import { Button } from './ui/button';
import { Label } from './ui/label';

// Inicializar Stripe (se inicializa una sola vez)
let stripePromiseInstance = null;
const getStripePromise = () => {
  if (!stripePromiseInstance) {
    stripePromiseInstance = loadStripe(STRIPE_PUBLIC_KEY);
  }
  return stripePromiseInstance;
};

// Componente del formulario de pago
const PaymentFormContent = ({ onSubmit, loading, error, amount, currency, beforeSubmit, disabled, submitLabel, intervalo }) => {
  /* El cobro no puede salir sin aceptar los términos: es requisito legal, y el
     mockup los pone aquí, justo antes del botón. */
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    try {
      // Validación previa (p. ej. datos de la cuenta): si no pasa, abortamos ANTES
      // de tokenizar la tarjeta y antes de cobrar — así no se crea nada a medias.
      if (beforeSubmit) {
        const ok = await beforeSubmit();
        if (!ok) {
          setProcessing(false);
          return;
        }
      }

      const cardElement = elements.getElement(CardElement);
      
      if (!cardElement) {
        throw new Error('No se pudo obtener el elemento de tarjeta');
      }

      // Crear PaymentMethod directamente (esto lo guarda en Stripe)
      // IMPORTANTE: Asegurarse de que las claves de Stripe coincidan
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (stripeError) {
        console.error('❌ Error de Stripe al crear PaymentMethod:', stripeError);
        throw new Error(stripeError.message || 'Error al procesar la tarjeta');
      }

      if (!paymentMethod || !paymentMethod.id) {
        throw new Error('No se pudo crear el método de pago. Intenta nuevamente.');
      }

      // Verificar que el PaymentMethod tiene un ID válido
      if (!paymentMethod.id.startsWith('pm_')) {
        throw new Error('El método de pago no es válido. Intenta nuevamente.');
      }

      console.log('✅ PaymentMethod creado:', paymentMethod.id);
      console.log('✅ PaymentMethod tipo:', paymentMethod.type);
      console.log('✅ PaymentMethod card:', paymentMethod.card?.brand, '****', paymentMethod.card?.last4);
      
      // Esperar un momento para asegurar que Stripe procese el PaymentMethod
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Llamar al callback con el paymentMethod
      await onSubmit(paymentMethod);
    } catch (err) {
      console.error('❌ Error en pago:', err);
      throw err;
    } finally {
      setProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#0E0E10',  // tinta (BRAND.md §1)
        '::placeholder': {
          color: '#6E6E77',  // pizarra
        },
        // Inter, para que el campo de tarjeta no desentone con el resto (BRAND.md §2)
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      },
      invalid: {
        color: '#E23434',  // alerta (BRAND.md §1)
        iconColor: '#E23434',
      },
    },
    hidePostalCode: true,
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label style={{ color: '#0E0E10', fontWeight: 600 }}>Información de tarjeta</Label>
        {/* Píldora, como el resto de campos del sistema (BRAND.md §4: inputs ~11px,
            pero aquí el mockup usa la misma forma redondeada de los demás campos). */}
        <div
          style={{
            background: '#fff',
            border: '1px solid #E7E7EC',
            borderRadius: 9999,
            padding: '18px 22px',
          }}
        >
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
          <p className="text-red-500 text-sm">{error}</p>
        </div>
      )}

      {/* Términos, como en el mockup: justo antes del botón de pago. */}
      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          cursor: 'pointer',
          padding: '0 2px',
        }}
      >
        <input
          type="checkbox"
          checked={aceptaTerminos}
          onChange={(e) => setAceptaTerminos(e.target.checked)}
          style={{ marginTop: 3, width: 17, height: 17, accentColor: '#F73E48', flex: 'none' }}
        />
        <span style={{ fontSize: 12.5, color: '#6E6E77', lineHeight: 1.5 }}>
          Acepto los{' '}
          <a href="/terms" target="_blank" rel="noreferrer" style={{ color: '#0E0E10', fontWeight: 700, textDecoration: 'underline' }}>
            Términos y condiciones
          </a>{' '}
          y el{' '}
          <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: '#0E0E10', fontWeight: 700, textDecoration: 'underline' }}>
            Aviso de Privacidad
          </a>
        </span>
      </label>

      {/*
        Botón secundario del manual (BRAND.md §6): fondo blanco, borde y texto
        en color de marca. Es el que usa el mockup para «PAGAR y ACCEDER» —
        deliberadamente menos estridente que el rojo sólido, porque a esta
        altura el argumento ya está hecho arriba con la cifra de ahorro.
      */}
      <Button
        type="submit"
        disabled={!stripe || processing || loading || disabled || !aceptaTerminos}
        style={{
          width: '100%',
          height: 58,
          borderRadius: 9999,
          background: '#fff',
          border: '1.5px solid #F73E48',
          color: '#F73E48',
          fontSize: 17,
          fontWeight: 700,
          letterSpacing: '0.01em',
          boxShadow: '0 10px 26px -12px rgba(247,62,72,0.45)',
          opacity: (!stripe || processing || loading || disabled || !aceptaTerminos) ? 0.5 : 1,
          cursor: (!stripe || processing || loading || disabled || !aceptaTerminos) ? 'not-allowed' : 'pointer',
        }}
      >
        {processing || loading ? 'Procesando...' : (submitLabel || `PAGAR y ACCEDER`)}
      </Button>

      {/*
        Iván (13-ago-2026): «abajo del cobro que diga: desbloquea todo y durante
        X tienes acceso completo». La X es el plan que tenga elegido en ese
        momento, no un texto fijo — si dijera siempre «un año», al que va a pagar
        el mensual le estaríamos prometiendo once meses de más.
      */}
      {intervalo && (
        <p style={{ textAlign: 'center', fontSize: 13, color: '#33333A', margin: '0 0 6px', fontWeight: 600 }}>
          Desbloquea todo y durante {intervalo === 'year' ? 'un año' : 'un mes'} tienes acceso completo.
        </p>
      )}

      <p style={{ textAlign: 'center', fontSize: 12, color: '#6E6E77', margin: 0 }}>
        🔒 Pago seguro con Stripe · Cancela cuando quieras
      </p>
    </form>
  );
};

// Componente wrapper con Elements
const PaymentForm = ({ onSubmit, loading, error, amount, currency, beforeSubmit, disabled, submitLabel, intervalo }) => {
  return (
    <Elements stripe={getStripePromise()}>
      <PaymentFormContent
        onSubmit={onSubmit}
        loading={loading}
        error={error}
        amount={amount}
        currency={currency}
        beforeSubmit={beforeSubmit}
        disabled={disabled}
        submitLabel={submitLabel}
        intervalo={intervalo}
      />
    </Elements>
  );
};

export default PaymentForm;

